import { useEffect, useState } from "react";
import { X, Receipt, WarningCircle, BookOpen, Question } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* ============================== PASSBOOK MODAL ============================== */
export function PassbookModal({ kind, item, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const path = kind === "pass" ? `/passes/${item.id}/passbook`
               : kind === "card" ? `/cards/${item.id}/passbook`
               : `/transit-cards/${item.id}/passbook`;
    api.get(path).then(({ data }) => setData(data)).catch(() => toast.error("Failed to load passbook"))
      .finally(() => setLoading(false));
  }, [kind, item.id]);

  if (!item) return null;
  return (
    <ModalShell title="Passbook" subtitle={item.pass_no || item.card_no} onClose={onClose} icon={<Receipt size={18}/>}>
      {loading && <div className="text-center text-slate-400 py-8 text-sm">Loading transactions…</div>}
      {!loading && data && (
        <>
          <div className="grid grid-cols-3 gap-3 bg-slate-50 border border-slate-200 rounded-md p-3">
            <KV label="Holder" value={data.holder_name}/>
            <KV label={kind === "pass" ? "Category" : "Type"} value={data.category || data.card_type}/>
            <KV label="Status" value={<StatusPill s={data.status}/>}/>
            {data.balance !== undefined && <KV label="Balance" value={`₹${data.balance}`}/>}
          </div>
          <div>
            <div className="label-cap text-slate-500 mb-2">Transaction History · {data.history?.length || 0}</div>
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-[55vh] overflow-auto">
              {(data.history || []).map((h, i) => (
                <div key={h.id || i} className="flex items-start gap-3 p-3 text-sm hover:bg-slate-50">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold uppercase ${kindBadge(h.kind)}`}>
                    {(h.kind||"tx").slice(0,1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 capitalize">{prettyKind(h.kind)}</div>
                    <div className="text-xs text-slate-500">{new Date(h.at).toLocaleString("en-IN")} · by {h.by}</div>
                    {h.new_valid_to && <div className="text-xs text-slate-600">New valid to: <b>{h.new_valid_to}</b></div>}
                    {h.new_balance !== undefined && <div className="text-xs text-slate-600">New balance: <b>₹{h.new_balance}</b></div>}
                    {h.reason && <div className="text-xs text-red-600">Reason: {h.reason}{h.fir_number && ` · FIR ${h.fir_number}`}</div>}
                  </div>
                  {h.amount !== undefined && (
                    <div className="text-right">
                      <div className={`font-display font-bold text-base tabular-nums ${h.kind==="topup"||h.kind==="issue"||h.kind==="renew"?"text-emerald-700":"text-slate-700"}`}>
                        {h.kind==="lost" || h.kind==="cancel" ? "—" : `₹${h.amount}`}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {(!data.history || data.history.length === 0) &&
                <div className="text-center py-8 text-sm text-slate-400 italic">No transactions yet</div>}
            </div>
          </div>
        </>
      )}
    </ModalShell>
  );
}

/* ============================ REPORT LOST MODAL ============================ */
export function ReportLostModal({ kind, item, onClose, onDone }) {
  const [reason, setReason] = useState("Lost / Misplaced");
  const [firNumber, setFirNumber] = useState("");
  const [busy, setBusy] = useState(false);

  if (!item) return null;
  const reasons = ["Lost / Misplaced", "Stolen / Theft", "Damaged Beyond Use", "Card Reader Not Reading", "Other"];

  const submit = async () => {
    setBusy(true);
    try {
      const path = kind === "pass" ? `/passes/${item.id}/report-lost`
                 : kind === "card" ? `/cards/${item.id}/report-lost`
                 : `/transit-cards/${item.id}/report-lost`;
      await api.post(path, { reason, fir_number: firNumber });
      toast.success(`${kind === "pass" ? "Pass" : "Card"} ${item.pass_no || item.card_no} marked Lost. Replacement can now be issued.`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to report lost");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={`Report Lost ${kind === "pass" ? "Pass" : "Card"}`} subtitle={item.pass_no || item.card_no} onClose={onClose} icon={<WarningCircle size={18} className="text-red-600"/>}>
      <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm">
        <div className="font-semibold text-red-800">Important</div>
        <div className="text-xs text-red-700 mt-1">Marking this as Lost will block it from being used. The holder must visit a counter with ID proof to get a replacement issued. {kind !== "pass" && "Balance will be transferred to the replacement card."}</div>
      </div>
      <div>
        <div className="label-cap text-slate-500 mb-2">Reason *</div>
        <div className="grid grid-cols-2 gap-2">
          {reasons.map(r => (
            <button key={r} onClick={()=>setReason(r)}
                    className={`px-3 py-2 border rounded-md text-sm text-left ${reason===r?"border-red-500 bg-red-50 font-semibold":"border-slate-200 hover:bg-slate-50"}`}>
              {r}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="label-cap text-slate-500 mb-2">FIR / Police Complaint # (optional)</div>
        <input data-testid="report-lost-fir" value={firNumber} onChange={e=>setFirNumber(e.target.value)} placeholder="e.g. FIR/2026/0123"
               className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
      </div>
      <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-5 px-5 py-3 mt-2 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Cancel</button>
        <button data-testid="report-lost-submit" disabled={busy} onClick={submit}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-semibold disabled:opacity-60">
          {busy ? "Processing…" : "Report Lost"}
        </button>
      </div>
    </ModalShell>
  );
}

/* ============================== INFO MODAL (T&C / FAQ) ============================== */
export function InfoModal({ title, kind, fallbackSections, onClose, icon, canEdit = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!kind) { setData({ draft_sections: fallbackSections || [], published_sections: fallbackSections || [], is_published: true, has_unpublished_changes: false, version: 0 }); setLoading(false); return; }
    api.get(`/content/${kind}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ draft_sections: fallbackSections || [], published_sections: fallbackSections || [], is_published: false, has_unpublished_changes: false, version: 0 }))
      .finally(() => setLoading(false));
  }, [kind, fallbackSections]);

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(data?.draft_sections || data?.published_sections || []))); setEditing(true); };
  const cancelEdit = () => setEditing(false);

  const saveDraft = async () => {
    if (!kind) return;
    setSaving(true);
    try {
      await api.put(`/content/${kind}`, { sections: draft });
      const { data: fresh } = await api.get(`/content/${kind}`);
      setData(fresh); setEditing(false);
      toast.success("Draft saved");
    } catch { toast.error("Failed to save draft"); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (!kind) return;
    setSaving(true);
    try {
      if (editing) await api.put(`/content/${kind}`, { sections: draft });
      await api.post(`/content/${kind}/publish`);
      const { data: fresh } = await api.get(`/content/${kind}`);
      setData(fresh); setEditing(false);
      toast.success("Published — now live on public website / iOS / Android");
    } catch { toast.error("Publish failed"); }
    finally { setSaving(false); }
  };

  const unpublish = async () => {
    if (!kind) return;
    if (!window.confirm("Hide this content from the public website / iOS / Android until you publish again?")) return;
    setSaving(true);
    try {
      await api.post(`/content/${kind}/unpublish`);
      const { data: fresh } = await api.get(`/content/${kind}`);
      setData(fresh);
      toast.success("Unpublished — hidden from public");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const addSection = () => setDraft([...draft, { title: "New Section", body: "" }]);
  const removeSection = (i) => setDraft(draft.filter((_, idx) => idx !== i));
  const updateSection = (i, key, val) => setDraft(draft.map((s, idx) => idx === i ? { ...s, [key]: val } : s));
  const moveSection = (i, dir) => {
    const next = [...draft];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setDraft(next);
  };

  return (
    <ModalShell title={title} onClose={onClose} icon={icon}
                subtitle={data && (
                  <span className="inline-flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${data.is_published?"bg-green-100 text-green-700":"bg-slate-100 text-slate-500"}`}>
                      {data.is_published ? "🟢 Live on public apps" : "⚪ Not published"}
                    </span>
                    <span className="text-[10px]">v{data.version}</span>
                    {data.has_unpublished_changes && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">Draft has unpublished changes</span>}
                  </span>
                )}
                actions={canEdit && kind && !editing && (
                  <div className="flex gap-2">
                    <button onClick={startEdit} className="px-3 py-1 border border-slate-200 rounded text-xs hover:bg-slate-50">Edit</button>
                    {data?.is_published
                      ? <button onClick={unpublish} disabled={saving} className="px-3 py-1 border border-red-200 text-red-700 rounded text-xs hover:bg-red-50">Unpublish</button>
                      : <button onClick={publish} disabled={saving} className="px-3 py-1 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700">Activate / Publish</button>}
                    {data?.has_unpublished_changes && <button onClick={publish} disabled={saving} className="px-3 py-1 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700">Publish draft</button>}
                  </div>
                )}>
      {loading && <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>}
      {!loading && !editing && (
        <div className="space-y-4 max-h-[65vh] overflow-auto">
          {(data?.draft_sections || data?.published_sections || []).map((s, i) => (
            <div key={i}>
              <div className="font-display font-bold text-base text-[#1E3A8A] mb-1">{s.title}</div>
              <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{s.body}</div>
            </div>
          ))}
        </div>
      )}
      {!loading && editing && (
        <div className="space-y-3 max-h-[60vh] overflow-auto">
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            ⚠️ You are editing the <b>draft</b>. Click <b>Publish draft</b> to push these changes live to the customer website / iOS / Android apps.
          </div>
          {draft.map((s, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <input value={s.title} onChange={e=>updateSection(i, "title", e.target.value)} placeholder="Section Title"
                       className="flex-1 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm font-semibold outline-none"/>
                <button onClick={()=>moveSection(i,-1)} disabled={i===0} className="text-xs px-2 py-1 border border-slate-200 rounded disabled:opacity-30">↑</button>
                <button onClick={()=>moveSection(i,1)} disabled={i===draft.length-1} className="text-xs px-2 py-1 border border-slate-200 rounded disabled:opacity-30">↓</button>
                <button onClick={()=>removeSection(i)} className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50">Remove</button>
              </div>
              <textarea value={s.body} onChange={e=>updateSection(i, "body", e.target.value)} placeholder="Section body content (multi-line allowed)"
                        rows={4} className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded text-sm outline-none font-mono leading-relaxed"/>
            </div>
          ))}
          <button onClick={addSection} className="w-full py-2 border-2 border-dashed border-slate-300 rounded-md text-sm text-slate-600 hover:bg-slate-50">+ Add Section</button>
          <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-5 px-5 py-3 flex justify-end gap-2">
            <button onClick={cancelEdit} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Cancel</button>
            <button onClick={saveDraft} disabled={saving} className="px-4 py-2 border border-slate-200 rounded-md text-sm">{saving?"Saving…":"Save Draft"}</button>
            <button onClick={publish} disabled={saving} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-semibold">{saving?"Publishing…":"Save & Publish 🟢"}</button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

/* ============================== T&C / FAQ CONTENT ==============================
   Source: KTCL Cashless Portal (cashless.ktcl.goa.gov.in) — verbatim where possible. */
export const PASS_TERMS = [
  { title: "1. Pricing Policy",
    body: "Registration Fee (one-time, non-refundable): ₹35.\n"
        + "Pass fare is computed per-route using GTFS fare × 60 trips/month × category discount.\n"
        + "Commuters (General): 40% discount on the general fare.\n"
        + "Students & Senior Citizens: 70% discount on each travel.\n"
        + "Person with Disability / Freedom Fighter / KTCL Staff: Free.\n"
        + "Total payable at issuance = Pass Fare(s) + Registration Fee." },
  { title: "2. Validity & Travel",
    body: "Pass is valid for 2 journeys per day on the route(s) selected at issuance.\n"
        + "Pass is valid only on KTCL buses operating on the selected route(s) and bus services\n"
        + "(SHUTTLE / SHUTTLE EV / LOCAL / LOCAL EV / SHUTTLE SPECIAL). AC / Luxury services excluded\n"
        + "unless explicitly added." },
  { title: "3. Eligibility & Non-Transferability",
    body: "Smart Transit Pass is issued to residents of Goa and persons working in Goa subject to\n"
        + "document verification. The Pass is strictly non-transferable. Government and Aided-\n"
        + "Institution employees are NOT eligible for the concessional pass scheme." },
  { title: "4. Multi-Route Passes",
    body: "A single Pass may cover up to 5 designated routes. The amount on each route is computed\n"
        + "independently using the same formula. Routes may be added or replaced only at the time of\n"
        + "renewal — not mid-validity." },
  { title: "5. Pass Renewal",
    body: "Renew at any KTCL counter (Margao / Panaji / Vasco / Mapusa) or via the MajheApp before\n"
        + "the validity expires. Same documents are re-verified at renewal." },
  { title: "6. Loss / Damage / Replacement",
    body: "If the Pass is lost, damaged or stolen, report it to the issuing counter immediately. A\n"
        + "replacement pass can be issued on submission of ID proof + replacement fee." },
  { title: "7. Concession Misuse",
    body: "Concessional passes misused will be cancelled, the full fare differential recovered, and\n"
        + "the Pass will be subject to immediate seizure. Legal action may follow." },
  { title: "8. Refund Rules",
    body: "Registration Fee is strictly non-refundable. Pass fare is non-refundable once issued.\n"
        + "Errors or disputes must be reported to customer service within 48 hours of the transaction." },
  { title: "9. Identity Proof & Conductor's Authority",
    body: "The Pass holder must always carry a valid government-issued photo ID (Aadhaar / DL / Voter\n"
        + "ID / PAN / Passport) along with the Pass while travelling. Conductors may verify the Pass\n"
        + "and the holder's identity at any time; inability to produce the Pass on demand will be\n"
        + "treated as ticketless travel and the Pass will be subject to immediate seizure." },
];

export const PASS_FAQ = [
  { title: "How much does a Smart Pass cost?",
    body: "₹35 one-time registration fee + Pass fare. Fare = single-route fare × 60 trips × "
        + "(1 − discount). Commuters get 40% off; Students & Senior Citizens get 70% off; PwD, "
        + "Freedom Fighters & KTCL Staff travel free." },
  { title: "What is the Smart Transit Pass?",
    body: "Your monthly or yearly KTCL travel pass — valid only on Designated Routes selected by you "
        + "at the time of issuance. You can buy a single-route pass or combine up to 5 routes on the "
        + "same pass." },
  { title: "Where can I apply for a Smart Transit Pass?",
    body: "From the MajheApp Pass screen (apply, recharge, renew online) OR walk in to any KTCL "
        + "counter — Margao, Panaji, Vasco, Mapusa." },
  { title: "What documents do I need?",
    body: "All applicants: Aadhaar Card + passport-size photograph. Concession categories also need: "
        + "Social Welfare Certificate (Senior / Freedom Fighter / PwD), Institution ID (Student), "
        + "Govt-issued ID card (Govt. Employee), KTCL Employee ID (KTCL Staff). Working-in-Goa "
        + "private applicants additionally need a Company Bonafide certificate OR Police Verification." },
  { title: "How long does issuance take?",
    body: "About 15-20 minutes at the counter once documents are verified. Online applications are "
        + "reviewed by KTCL within 24 hours; you can collect the physical card from your chosen depot "
        + "thereafter. Renewals are instant." },
  { title: "Can I use the Pass on any KTCL bus?",
    body: "Yes — on the bus services you selected at issuance (SHUTTLE / SHUTTLE EV / LOCAL / "
        + "LOCAL EV / SHUTTLE SPECIAL), and only on the route(s) you selected. AC / Luxury services "
        + "are excluded by default." },
  { title: "How many trips per day are covered?",
    body: "Two journeys per day on each designated route, throughout the pass validity." },
  { title: "What if I lose my Pass?",
    body: "Report it to the issuing counter or via MajheApp immediately. Optionally file a police "
        + "complaint. A replacement pass is issued on submission of ID proof + nominal replacement fee." },
  { title: "Can I get a refund if I cancel?",
    body: "Registration Fee is non-refundable. Once issued the pass fare is non-refundable. Disputes "
        + "must be raised within 48 hours via customer service." },
  { title: "Is the Pass transferable?",
    body: "No. The Pass is strictly non-transferable. Misuse leads to cancellation, recovery of full "
        + "fare and immediate seizure of the Pass." },
];

export const TRANSIT_CARD_TERMS = [
  { title: "1. Pricing Policy", body: "Registration fee for new Smart Transit Card is ₹150 (one-time). The full ₹150 is credited back as opening wallet balance — so the customer pays ₹150 and walks away with ₹150 spendable balance on the card. Minimum top-up/recharge afterwards is ₹100 per transaction. Maximum top-up ₹10,000 per transaction." },
  { title: "2. Card Issuance", body: "Smart Transit Card is issued by KTCL upon payment of the ₹150 registration fee, which is gifted back in full as opening balance. The card carries a 16-digit unique number and is valid for 10 years from the date of issue. An optional extra top-up may be loaded at issuance (minimum ₹100 if added)." },
  { title: "3. Stored-Value & NCMC", body: "The card is a stored-value transit card built on NCMC standards. Balance is maintained on the card / linked account and is debited automatically when tapped on the conductor's ETM device on board." },
  { title: "4. Recharge / Top-up Policy", body: "Cards can be topped up at any KTCL counter or via the KTCL passenger app. Minimum top-up ₹100, maximum ₹10,000 per transaction. Wallet maximum ₹10,000 at any time." },
  { title: "5. Card Categories & Concessions", body: "General Public card carries full fare. Student, Senior Citizen and PwD cards carry the same concession discounts as the corresponding Passes. Submit proof documents at issuance — concession is recorded on the card." },
  { title: "6. Loss / Damage / Replacement", body: "Report a lost or damaged card to any KTCL counter immediately. The card will be blocked. Balance is transferred to the replacement card after KYC verification. Replacement fee: ₹150 (credited back as balance like the original)." },
  { title: "7. Refund Rules", body: "1. Registration fee is converted to wallet balance and is therefore not refundable as cash. The balance can however be fully spent on KTCL services until exhausted.\n2. All amounts added to the card through top-up are strictly non-refundable. Once a top-up is completed, the funds cannot be withdrawn, refunded or transferred to another account or card.\n3. The funds can only be used for KTCL services. The balance remains available until fully utilised.\n4. Errors or disputes must be reported to customer service within 48 hours of the transaction. KTCL reserves the right to final judgment on any dispute." },
  { title: "8. Card Misuse", body: "Any misuse of the card (sharing, concession misuse, tampering) will result in card blocking, balance forfeiture and recovery of full fare. Legal action may follow." },
  { title: "9. Data Privacy", body: "Personal data submitted during registration is used only for issuance and travel-related communication. KTCL adheres to all applicable data-protection regulations." },
];

export const TRANSIT_CARD_FAQ = [
  { title: "How much does a Smart Transit Card cost?", body: "₹150 registration fee — and KTCL credits the full ₹150 back as opening wallet balance. So the customer pays ₹150 at the counter and walks away with ₹150 of spendable travel balance on the card. You may optionally add more top-up at issuance (minimum ₹100)." },
  { title: "How is a Smart Transit Card different from a Pass?", body: "A Pass is fixed-duration unlimited travel (Daily/Monthly/Yearly etc.) within selected route. A Card is a stored-value wallet — pay-per-trip, balance debited automatically when you tap. Use whichever suits your travel pattern best." },
  { title: "Where can I apply?", body: "At any of the four KTCL counters — Margao, Panaji, Vasco or Mapusa. Bring a Government ID + a passport-size photograph. Concession categories need additional proof documents." },
  { title: "How do I recharge / top-up?", body: "At any KTCL counter, or in the KTCL passenger app. Minimum recharge ₹100, maximum ₹10,000 per transaction. Wallet cap ₹10,000 at any time. Top-up amounts are non-refundable." },
  { title: "How do I check balance?", body: "Use the Balance Lookup widget on the Smart Transit Card screen — enter card number or registered mobile. Conductors can also tell you the post-trip balance after each tap." },
  { title: "Card lost or stolen — what now?", body: "Visit any KTCL counter or use 'Report Lost' in the admin panel. The card is blocked instantly. A replacement card carrying the unspent balance is issued on submission of ID proof + ₹150 replacement fee (also credited back as balance)." },
  { title: "Can I get a refund for top-up?", body: "No. All top-up amounts (including the initial ₹150 fee credited as balance) are strictly non-refundable as cash. The balance remains usable on KTCL services until fully spent." },
  { title: "Is the card valid on private buses?", body: "No, the Smart Transit Card is currently accepted only on KTCL fleet. Private operator integration is on the roadmap." },
  { title: "Can I link two mobile numbers to one card?", body: "Only one registered mobile number per card. Update it at any KTCL counter with valid ID proof." },
];

/* ============================== shared building blocks ============================== */
const ModalShell = ({ title, subtitle, onClose, icon, children, actions }) => (
  <div className="fixed inset-0 bg-black/50 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
    <div data-testid="passbook-modal" className="bg-white w-full max-w-2xl max-h-[90dvh] sm:max-h-[92vh] overflow-auto rounded-t-2xl sm:rounded-lg" onClick={e=>e.stopPropagation()}>
      <div className="sticky top-0 bg-white p-4 sm:p-5 border-b border-slate-200 flex items-start justify-between gap-3 z-10 rounded-t-2xl sm:rounded-t-lg">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {icon}
          <div className="min-w-0">
            <h3 className="font-display text-xl font-bold">{title}</h3>
            {subtitle && <div className="text-xs text-slate-500 mt-0.5 font-mono">{subtitle}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
        </div>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  </div>
);

const KV = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">{label}</div>
    <div className="text-sm font-medium text-slate-800 mt-0.5">{value}</div>
  </div>
);

const StatusPill = ({ s }) => {
  const map = { Issued:"bg-green-50 text-green-700", Active:"bg-green-50 text-green-700",
                Renewed:"bg-blue-50 text-blue-700", Lost:"bg-red-50 text-red-700",
                Cancelled:"bg-slate-100 text-slate-500", Expired:"bg-amber-50 text-amber-700" };
  return <span className={`text-[11px] px-2 py-0.5 rounded font-semibold ${map[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;
};

const prettyKind = (k) => ({ issue:"Issued", renew:"Renewed", topup:"Top-up", lost:"Reported Lost",
                              cancel:"Cancelled" }[k] || k);
const kindBadge = (k) => ({ issue:"bg-blue-100 text-blue-700",
                             renew:"bg-purple-100 text-purple-700",
                             topup:"bg-emerald-100 text-emerald-700",
                             lost:"bg-red-100 text-red-700",
                             cancel:"bg-slate-200 text-slate-600" }[k] || "bg-slate-200 text-slate-600");

/* Re-export common icons so callers don't need a second import */
export const InfoIcons = { BookOpen, Question };
